import { Stack, styled } from '@mui/material'
import { useSelectUserBirthYear, useSelectUserGender } from '../../../../state/selectors'
import { theme } from '../../../../theme/theme'
import { ScreeningViewTemplate } from '../ScreeningTemplate'
import { TextInput } from '../../../inputs/Text'
import { useState } from 'react'
import { formatPersonalNumber } from '../../../../utils/personalNumber'
import { ScreeningType, isValidPersonalNumberFormat, TrackingEvents } from '@estercare/ester-shared'
import { useScreeningDescriptionText, useStartScreening } from '../ScreeningShared'
import {
  JournalConsentCheckbox,
  OptionalJournalSection,
  useScreeningSummaryOptIn,
} from '../../../consent/JournalConsent'
import { useScreeningConsent, ScreeningConsentCheckbox } from '../../ScreeningConsent'
import { useTranslation } from 'react-i18next'
import { useEmitEvent } from '../../../../hooks/analytics/useEmitEvent'
import { ConsentId } from '../../../../api/ester-b2b-api/private/consent/types'
import { queryErrorHandlerWithMessage } from '../../../../utils/queryErrorHandler'
import { userIsUnder15 } from '../../../../utils/ageChecks'
import { BodyLarge } from '../../../styles/Typography'
import { Info } from '../../../icons'
import { Gender } from '../../../../api/ester-b2b-api/private/users/types'
import { MaleScreeningView } from './MaleScreeningView'

export const ScreeningStandardNotStarted = () => {
  const { t } = useTranslation()
  const screeningDescription = useScreeningDescriptionText(ScreeningType.STANDARD)
  const emitEvent = useEmitEvent()

  const { consentData, isConsentLoading, consentCheckbox, setConsentCheckbox, setUserConsent, isButtonDisabled } =
    useScreeningConsent()
  const summaryOptIn = useScreeningSummaryOptIn()
  const birthYear = useSelectUserBirthYear()
  const isMaleUser = useSelectUserGender() === Gender.M
  const [error, setError] = useState<string | null>(null)
  const [personalNumber, setPersonalNumber] = useState('')

  const { startScreening, isStartPending } = useStartScreening()
  const isPersonalNumberValid = isValidPersonalNumberFormat(personalNumber)
  const { askForPersonalNumber } = summaryOptIn
  const isPersonalNumberMissing = askForPersonalNumber && !isPersonalNumberValid

  const onPersonalNumberChange = (value: string) => {
    setPersonalNumber(formatPersonalNumber(value))
  }
  const userUnder15 = birthYear ? userIsUnder15(birthYear) : false // default to false if no birthyear, since its more probable someone is over 15 than under.
  const onButtonClick = async () => {
    emitEvent({
      eventType: TrackingEvents.CLICK,
      action: 'standard_start_screening',
    })

    const saveOptInAndStart = async () => {
      try {
        await summaryOptIn.saveOptIn()
      } catch (error) {
        setError(queryErrorHandlerWithMessage('save screening summary consent', t)(error as Error))
        return
      }
      await startScreening(ScreeningType.STANDARD, setError, askForPersonalNumber ? personalNumber : undefined)
    }

    if (!consentData) {
      setUserConsent(ConsentId.SCREENING_CONSENT, {
        onSuccess: saveOptInAndStart,
        onError: (error) => {
          const message = queryErrorHandlerWithMessage('set user screening consent and start screening', t)(error)
          setError(message)
        },
      })
    } else {
      await saveOptInAndStart()
    }
  }

  if (isMaleUser) {
    return <MaleScreeningView />
  }

  return (
    <ScreeningViewTemplate
      pageTitle={t('employee.screening.standardNotStartedPage')}
      screeningTitle={t('employee.screening.standardTitle')}
      screeningDescription={screeningDescription}
      screeningAdditionalChildren={
        userUnder15 ? (
          <Stack textAlign="left" gap={`${theme.spacing.small}px`} direction="row" alignItems="center">
            <Info color={theme.color.plumMid} size={32} />
            <StyledBodyLarge>{t('employee.screening.under15Warning')}</StyledBodyLarge>
          </Stack>
        ) : (
          <Stack textAlign="left" gap={`${theme.spacing.mediumLarge}px`}>
            {!isConsentLoading && !consentData && (
              <ScreeningConsentCheckbox consentCheckbox={consentCheckbox} setConsentCheckbox={setConsentCheckbox} />
            )}
            {(summaryOptIn.showOptIn || askForPersonalNumber) && (
              <OptionalJournalSection>
                {summaryOptIn.showOptIn && (
                  <JournalConsentCheckbox
                    title={t('employee.screening.journalTitle')}
                    text={t('employee.screening.journalText')}
                    label={t('employee.screening.journalCheckbox')}
                    checked={summaryOptIn.checked}
                    onChange={summaryOptIn.setChecked}
                  />
                )}
                {askForPersonalNumber && (
                  <TextInput
                    label={t('common.form.personalNumber')}
                    onChange={(e) => onPersonalNumberChange(e.target.value)}
                    value={personalNumber || ''}
                    placeholder={birthYear ? `${birthYear}XXXX-XXXX` : '19XXXXXX-XXXX'}
                    borderColor={isPersonalNumberValid ? theme.color.green : undefined}
                  />
                )}
              </OptionalJournalSection>
            )}
          </Stack>
        )
      }
      buttonText={t('employee.screening.standardNotStartedButton')}
      onButtonClick={onButtonClick}
      isButtonDisabled={userUnder15 || isButtonDisabled || isPersonalNumberMissing}
      isLoading={isStartPending}
      errorMsg={error || undefined}
      showFaq
    />
  )
}

const StyledBodyLarge = styled(BodyLarge)`
  color: ${theme.color.plumMid};
  text-align: left;
`
